import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { SUBSCRIPTION_TIERS, CREDIT_PURCHASE, WEEKLY_LIMITS } from "@/lib/subscription-tiers";

type BillingInterval = "monthly" | "yearly";

const PricingSection = () => {
  const [interval, setInterval] = useState<BillingInterval>("monthly");

  const plans = [
    {
      key: "essential" as const,
      tier: SUBSCRIPTION_TIERS.essential,
      featured: false,
      description: "Everything you need to start training your mind.",
      weeklyCredits: WEEKLY_LIMITS.essential,
    },
    {
      key: "elite" as const,
      tier: SUBSCRIPTION_TIERS.elite,
      featured: true,
      description: "For serious athletes who want maximum results.",
      weeklyCredits: WEEKLY_LIMITS.elite,
    },
  ];

  return (
    <section className="py-24 md:py-32" id="pricing">
      <div className="container px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.h2
            className="font-display text-3xl md:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Simple, transparent{" "}
            <span className="text-gradient-primary">pricing</span>
          </motion.h2>
          <motion.p
            className="text-muted-foreground text-lg mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Start with a 7-day free trial. Cancel anytime.
          </motion.p>

          <motion.div
            className="inline-flex items-center gap-2 rounded-full border border-border/50 p-1 bg-background/50"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={() => setInterval("monthly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                interval === "monthly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("yearly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                interval === "yearly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Yearly
            </button>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {plans.map((plan, i) => {
            const pricing = plan.tier[interval];
            return (
              <motion.div
                key={plan.key}
                className={`relative rounded-2xl p-8 border ${
                  plan.featured
                    ? "border-primary/40 glow-primary gradient-card"
                    : "border-border/50 gradient-card"
                } shadow-card`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-wide">
                    Most Popular
                  </span>
                )}

                <h3 className="font-display text-2xl font-bold mb-1">
                  {plan.tier.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-6">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <span className="font-display text-4xl font-bold">
                    ${pricing.price}
                  </span>
                  <span className="text-muted-foreground">
                    /{interval === "monthly" ? "mo" : "yr"}
                  </span>
                  {interval === "yearly" && "savings" in pricing && (
                    <span className="ml-2 text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                      {pricing.savings}
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-secondary-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.featured ? "hero" : "hero-outline"}
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <Link to="/signup">
                    Start Free Trial
                    <ArrowRight className="ml-1" />
                  </Link>
                </Button>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="max-w-3xl mx-auto mt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-border/50 bg-secondary/20">
            <Plus className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              Need more? Purchase additional credits for <span className="text-foreground font-medium">${CREDIT_PURCHASE.price} each</span>
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
